import { useCallback } from 'react'
import api from '../../lib/api'

type TutorialEventType =
  | 'tutorial_viewed'
  | 'tutorial_completed'
  | 'tutorial_rated'
  | 'quiz_completed'
  | 'search_performed'
  | 'help_button_clicked'
  | 'tour_started'
  | 'tour_abandoned'

export function useTutorialEvents() {
  const track = useCallback(
    async (eventType: TutorialEventType, tutorialId?: string, metadata?: Record<string, unknown>) => {
      try {
        await api.post('/tutorials/events', { eventType, tutorialId, metadata })
      } catch {
        // Silently fail — event tracking should not break the UX
      }
    },
    []
  )

  const trackViewed = useCallback(
    (tutorialId: string) => track('tutorial_viewed', tutorialId),
    [track]
  )

  const trackCompleted = useCallback(
    (tutorialId: string, durationSeconds?: number) =>
      track('tutorial_completed', tutorialId, durationSeconds ? { durationSeconds } : undefined),
    [track]
  )

  const trackRated = useCallback(
    (tutorialId: string, rating: number) =>
      track('tutorial_rated', tutorialId, { rating }),
    [track]
  )

  const trackQuizCompleted = useCallback(
    (quizId: string, score: number, total: number, passed: boolean) =>
      track('quiz_completed', undefined, { quizId, score, total, passed }),
    [track]
  )

  const trackSearch = useCallback(
    (query: string, resultCount: number) =>
      track('search_performed', undefined, { query, resultCount }),
    [track]
  )

  const trackHelpClicked = useCallback(
    (page: string) => track('help_button_clicked', undefined, { page }),
    [track]
  )

  const trackTourStarted = useCallback(
    (tutorialId: string, stepCount: number) =>
      track('tour_started', tutorialId, { stepCount }),
    [track]
  )

  const trackTourAbandoned = useCallback(
    (tutorialId: string, lastStep: number) =>
      track('tour_abandoned', tutorialId, { lastStep }),
    [track]
  )

  return {
    track,
    trackViewed,
    trackCompleted,
    trackRated,
    trackQuizCompleted,
    trackSearch,
    trackHelpClicked,
    trackTourStarted,
    trackTourAbandoned,
  }
}
